import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { cloudSchedulesQuery, runCloudNow, saveCloudSchedule, type CloudScheduleRow } from '../../api/admRuns'
import { toast } from '../../ui/notify'
import { STATE_PILL } from './AdmRunBits'

/**
 * ADM -> Discovery schedule -> Cloud collection: cand se aduc resursele AWS
 * si Azure in CMDB, pe firma, si "Run now" - cerinta 87.
 *
 * Colectarea e a cronului `collect_cloud` din ph4.api, care citeste orele de
 * aici; fara ore scrise, firma merge pe 06:15 si 18:15, ca inainte. Fiecare
 * cont isi scrie rularea in Discovery runs, felul "Cloud".
 *
 * Drepturile sunt ale programarilor de descoperire; `canWrite` spune doar daca
 * sa se deseneze butoanele, refuzul e pe server.
 */

const TITLE = 'Cloud collection'

function accounts(row: CloudScheduleRow): string {
  const parts: string[] = []
  if (row.aws > 0) parts.push(`${row.aws} AWS`)
  if (row.azure > 0) parts.push(`${row.azure} Azure`)
  return parts.join(', ')
}

function CloudRow({ row, canWrite, onSaved }: { row: CloudScheduleRow; canWrite: boolean; onSaved: () => void }) {
  const [editing, setEditing] = useState(false)
  const [times, setTimes] = useState(row.times.join(', '))
  const [active, setActive] = useState(row.active)
  const [busy, setBusy] = useState(false)
  const [asked, setAsked] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      const answer = await saveCloudSchedule(row.companyId, times, active)
      toast(answer.message, TITLE, 'success')
      setEditing(false)
      onSaved()
    } catch (error) {
      toast((error as Error).message, TITLE, 'error')
    } finally {
      setBusy(false)
    }
  }

  const runNow = async () => {
    setAsked(true)
    try {
      const answer = await runCloudNow(row.companyId)
      toast(answer.message, TITLE, answer.queued ? 'info' : 'success')
      onSaved()
    } catch (error) {
      setAsked(false)
      toast((error as Error).message, TITLE, 'error')
    }
  }

  return (
    <tr data-company={row.companyId}>
      <td>{row.companyName}</td>
      <td className="adm-muted">{accounts(row)}</td>
      <td className="adm-nowrap">
        {editing ? (
          <input
            type="text"
            className="form-control input-sm"
            aria-label="Times of day"
            placeholder="06:15, 18:15"
            value={times}
            onChange={(e) => setTimes(e.target.value)}
          />
        ) : (
          <>
            {row.times.join(', ')}
            {!row.custom ? <span className="adm-muted" style={{ fontSize: 11 }}> (default)</span> : null}
          </>
        )}
      </td>
      <td>
        {editing ? (
          <label style={{ fontWeight: 'normal', margin: 0 }}>
            <input type="checkbox" aria-label="Active" checked={active} onChange={(e) => setActive(e.target.checked)} /> On
          </label>
        ) : row.active ? (
          <span className="adm-pill adm-pill-ok">Yes</span>
        ) : (
          <span className="adm-pill">No</span>
        )}
      </td>
      <td className="adm-nowrap">
        {row.lastRunAt === '' ? (
          <span className="adm-muted">never</span>
        ) : (
          <>
            <a href={`/app/adm/run/${row.lastRunId}`}>{row.lastRunAt}</a>{' '}
            <span className={`adm-pill ${STATE_PILL[row.lastRunState] ?? ''}`}>{row.lastRunStateLabel}</span>
            {row.lastRunErrors > 0 ? <span className="text-danger"> {row.lastRunErrors} errors</span> : null}
          </>
        )}
      </td>
      <td className="adm-nowrap adm-muted">
        {row.runRequested || asked ? 'requested' : row.nextRunAt || '—'}
      </td>
      <td className="text-right adm-nowrap">
        {canWrite ? (
          editing ? (
            <>
              <button type="button" className="btn btn-success btn-xs" disabled={busy} onClick={() => void save()}>
                <i className="fas fa-check"></i> Save
              </button>{' '}
              <button
                type="button"
                className="btn btn-default btn-xs"
                disabled={busy}
                onClick={() => {
                  setEditing(false)
                  setTimes(row.times.join(', '))
                  setActive(row.active)
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-default btn-xs cloudEdit" onClick={() => setEditing(true)}>
                <i className="fas fa-pen"></i> Edit
              </button>{' '}
              <button
                type="button"
                className="btn btn-danger btn-xs cloudRunNow"
                disabled={asked || row.runRequested}
                title="Collect this customer's cloud accounts now"
                onClick={() => void runNow()}
              >
                <i className="fas fa-play"></i> Run now
              </button>
            </>
          )
        ) : null}
      </td>
    </tr>
  )
}

export function CloudCollectionPanel() {
  const page = useQuery(cloudSchedulesQuery())
  const client = useQueryClient()
  const rows = page.data?.rows ?? []

  if (page.isPending) return null
  /* Fara conturi de cloud nu e nimic de programat: panoul nu se deseneaza. */
  if (page.isError || rows.length === 0) return null

  const refresh = () => void client.invalidateQueries({ queryKey: ['adm', 'schedules', 'cloud'] })

  return (
    <div id="admCloudCollection" style={{ marginTop: 24 }}>
      <h4>
        <i className="fas fa-cloud"></i> {TITLE} <span className="adm-muted" style={{ fontSize: 12 }}>AWS and Azure, per customer</span>
      </h4>
      <table className="table table-condensed adm-list">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Accounts</th>
            <th>Times (server time)</th>
            <th>Active</th>
            <th>Last run</th>
            <th>Next run</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <CloudRow key={`${row.companyId}:${row.times.join(',')}:${row.active ? 1 : 0}`} row={row} canWrite={page.data?.canWrite ?? false} onSaved={refresh} />
          ))}
        </tbody>
      </table>
      <div className="adm-muted" style={{ fontSize: 12 }}>
        Without a schedule of its own a customer is collected at {(page.data?.defaultTimes ?? []).join(' and ')}. Every account writes its
        own run in Discovery runs, type Cloud, with what it found and what failed. <strong>Run now</strong> starts the collection right
        away when the cron server answers, otherwise on its next pass.
      </div>
    </div>
  )
}
